import {
  Color,
  Component,
  Dropdown,
  Icon,
  Image,
  Number,
  OptionEditor,
  Radio,
  RichText,
  Screen,
  Screens,
  Section,
  Slider,
  Switch,
  Textfield,
  MultiField,
  Textarea,
  Video,
} from '../formFields'

const DEFAULT_THUMBNAIL = 'https://demo-system-next.s3.amazonaws.com/assets/dxdemo/thumbnail.jpeg'
const PRODUCT_RELATED_COMPONENTS = [
  'productCard',
  'productDescription',
  'productAddToCartButton',
  'productImage',
  'productPrice',
  'productTitle',
  'productVideo',
]

const getButtonIcon = (withPrefix = false) => Icon({ name: withPrefix ? 'buttonIcon' : 'icon', label: 'Button icon' })

const getButtonAppearanceOptions = (withPrefix = false) => [
  Color({ name: withPrefix ? 'buttonTextColor' : 'textColor', label: withPrefix ? 'Button text color' : 'Text color' }),
  Color({
    name: withPrefix ? 'buttonBackgroundColor' : 'backgroundColor',
    label: withPrefix ? 'Button background color' : 'Background color',
  }),
  Color({
    name: withPrefix ? 'buttonBorderColor' : 'borderColor',
    label: withPrefix ? 'Button border color' : 'Border color',
  }),
]

const getButtonVariant = (withPrefix = false) =>
  Dropdown({
    name: withPrefix ? 'buttonVariant' : 'variant',
    label: withPrefix ? 'Button variant' : 'Variant',
    options: [
      { label: 'Primary', value: 'contained' },
      { label: 'Secondary', value: 'contained-tonal' },
      { label: 'Outlined', value: 'outlined' },
      { label: 'Quiet', value: 'text' },
      { label: 'Elevated', value: 'elevated' },
    ],
    filter: null,
  })

const fontWeightOptions = [
  { label: 'Light', value: 'light' },
  { label: 'Regular', value: 'regular' },
  { label: 'Bold', value: 'bold' },
  { label: 'Black', value: 'black' },
]

const imageResizeMode = Dropdown({
  name: 'imageResize',
  label: 'Image resize mode',
  options: [
    { label: 'Contain', value: 'contain' },
    {
      label: 'Cover',
      value: 'cover',
    },
  ],
  filter: null,
})

const eventType = Dropdown({
  name: 'events',
  label: 'Event Type',
  options: [
    { label: 'Product View', value: 'commerce.productViews' },
    { label: 'Product Add', value: 'commerce.productListAdds' },
    { label: 'Checkout', value: 'commerce.checkouts' },
    { label: 'Purchase', value: 'commerce.purchases' },
    { label: 'Empty Cart', value: 'emptyCart' },
    { label: 'Custom Event', value: 'customEvent' },
    { label: ' --- ', value: '' },
  ],
  filter: null,
})

const formId = Textfield({
  name: 'formId',
  label: 'Form ID (optional)',
  tooltip: 'Only use for multi-step forms OR if you need to reference data from a form in a different screen',
})

const appearanceMode = Dropdown({
  name: 'mode',
  label: 'Mode',
  options: [
    { label: 'Flat', value: 'flat' },
    { label: 'Outlined', value: 'outlined' },
  ],
})

const customEvent = Textfield({
  name: 'customEvent',
  label: 'Custom Event',
  hidden: (component) => component?.content?.events !== 'customEvent',
})

const alignmentOptions = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
]

const textProps = [
  Dropdown({
    name: 'size',
    label: 'Size',
    options: [
      { label: 'Small', value: 'small' },
      { label: 'Medium', value: 'medium' },
      { label: 'Large', value: 'large' },
    ],
    filter: null,
  }),
  Dropdown({
    name: 'weight',
    label: 'Weight',
    options: fontWeightOptions,
    filter: null,
  }),
  Dropdown({
    name: 'textAlignment',
    label: 'Text Alignment',
    options: alignmentOptions,
    filter: null,
  }),
]

export const banner = Component('banner', {
  name: 'banner',
  label: 'Banner',
  description: 'Displays an image with optional title, subtitle and button. Can link to other screen or product.',
  fields: [
    Image({ name: 'image', label: 'Image' }),
    Screen({ name: 'linkTo', label: 'Link to', filter: null, allowProduct: true, allowExternal: true }),
    Section(
      'Appearance',
      Slider({
        name: 'opacity',
        min: 0,
        max: 1,
        step: 0.01,
        displayAs: 'percentage',
        label: 'Overlay opacity',
        tooltip: 'Adjusting this value can help you provide better contrast between banner texts and the image',
      }),
      Dropdown({
        name: 'titleVariant',
        label: 'Title Size',
        options: [
          { label: 'Large', value: 'titleLarge' },
          { label: 'Middle', value: 'titleMedium' },
          { label: 'Small', value: 'titleSmall' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'subtitleVariant',
        label: 'Subtitle Size',
        options: [
          { label: 'Large', value: 'titleLarge' },
          { label: 'Medium', value: 'titleMedium' },
          { label: 'Small', value: 'titleSmall' },
        ],
        filter: null,
      }),
      imageResizeMode
    ),
    Section(
      'Alignment',
      Dropdown({
        name: 'horizontalAlignment',
        label: 'Horizontal Alignment',
        options: alignmentOptions,
        filter: null,
      }),
      Dropdown({
        name: 'contentAlignment',
        label: 'Content Alignment',
        options: alignmentOptions,
        filter: null,
      }),
      Dropdown({
        name: 'verticalAlignment',
        label: 'Vertical Alignment',
        options: [
          { label: 'Top', value: 'top' },
          { label: 'Middle', value: 'middle' },
          { label: 'Bottom', value: 'bottom' },
        ],
        filter: null,
      })
    ),
    Section('Text', Textfield({ name: 'title', label: 'Title' }), Textfield({ name: 'subtitle', label: 'Subtitle' })),
    Section(
      'Button',
      Switch({ name: 'showButton', label: 'Show button', defaultChecked: true }),
      { ...getButtonVariant(true), hidden: (cmp) => !cmp?.content?.showButton },
      Textfield({ name: 'buttonLabel', label: 'Button label', hidden: (cmp) => !cmp?.content?.showButton }),
      { ...getButtonIcon(true), hidden: (cmp) => !cmp?.content?.showButton },
      ...getButtonAppearanceOptions(true).map((field) => ({ ...field, hidden: (cmp) => !cmp?.content?.showButton }))
    ),
  ],
  exposedStyles: [
    Number({ name: 'height', label: 'Height (px)', min: 0, max: 1000 }),
    Color({ name: 'color', label: 'Text color' }),
  ],
  defaultContent: {
    content: {
      title: '',
      subtitle: '',
      titleVariant: 'headlineLarge',
      subtitleVariant: 'headlineMedium',
      linkTo: '',
      image: DEFAULT_THUMBNAIL,
      horizontalAlignment: 'center',
      verticalAlignment: 'middle',
      contentAlignment: 'center',
      imageResize: 'cover',
      showButton: true,
      buttonLabel: 'Click me',
    },
    styles: {
      height: 200,
    },
  },
})
const getButtonDataHandlingOptions = (withPrefix = false) =>
  Section(
    'Data handling',
    Screen({ name: 'linkTo', label: 'Navigate to', filter: null, allowProduct: true, allowExternal: true }),
    eventType,
    customEvent,
    formId,
    Textfield({ name: 'message', label: 'Message after click' })
  )
const getAlignmentOptions = (withPrefix = false) =>
  Dropdown({
    name: 'alignment',
    label: 'Alignment',
    options: [...alignmentOptions, { label: 'Stretch', value: 'stretch' }],
    filter: null,
  })
export const button = Component('button', {
  label: 'Button',
  description: 'Displays a button with specified label and visual variant. Can link to screen or product.',
  fields: [
    Textfield({ name: 'label', label: 'Label' }),
    getButtonVariant(),
    getButtonDataHandlingOptions(),
    Section('Appearance', getAlignmentOptions(), getButtonIcon(), ...getButtonAppearanceOptions()),
  ],
  exposedStyles: [Number({ name: 'borderRadius', label: 'Border radius (px)', min: 0, max: 100 })],
  defaultContent: {
    content: {
      label: 'Click',
      variant: 'outlined',
      events: ' --- ',
      message: '',
      alignment: 'stretch',
    },
  },
})

export const orderButton = Component('orderButton', {
  label: 'Order Button',
  description: 'Displays an order button with specified label and visual variant. Can link to screen.',
  category: 'Cart & Checkout',
  fields: [
    Textfield({ name: 'label', label: 'Label' }),
    Screen({ name: 'linkTo', label: 'Navigate to', filter: null, allowProduct: true }),
    eventType,
    Dropdown({
      name: 'alignment',
      label: 'Alignment',
      options: [
        { label: 'Center', value: 'center' },
        { label: 'Start', value: 'flex-start' },
        { label: 'End', value: 'flex-end' },
      ],
      filter: null,
    }),

    Textfield({ name: 'message', label: 'Message' }),
    Color({ name: 'buttonTextColor', label: 'Text Color' }),
  ],
  defaultContent: {
    content: {
      label: 'Click',
      events: ' --- ',
      message: '',
    },
  },
})

export const card = Component('card', {
  label: 'Card',
  fields: [
    Dropdown({
      name: 'cardType',
      label: 'Card Type',
      options: [
        { label: 'Image and text in one line', value: 'image-text-one-line' },
        { label: 'Image and text overlay', value: 'overlay' },
        { label: 'Image and text below', value: 'stacked' },
        { label: 'Image partially overlayed', value: 'partial-overlay' },
      ],
      filter: null,
    }),
    Textfield({ name: 'title', label: 'Title' }),
    Switch({ name: 'isSubtitleRichText', label: 'Subtitle is HTML', defaultChecked: false }),
    Textfield({
      name: 'subtitle',
      label: 'Subtitle',
      hidden: (cmp) => !!cmp?.content?.isSubtitleRichText,
    }),
    RichText({
      name: 'subtitle',
      label: 'Subtitle (HTML)',
      hidden: (cmp) => !cmp?.content?.isSubtitleRichText,
    }),
    Screen({ name: 'link', label: 'Link to', filter: null, allowExternal: true, allowProduct: true }),
    Section(
      'Image',
      Image({ name: 'image', label: 'Image' }),
      Number({
        name: 'imgHeight',
        label: 'Image height',
        min: 0,
        max: 1000,
      }),
      imageResizeMode
    ),
    Section(
      'Button',
      Switch({ name: 'showButton', label: 'Show button', defaultChecked: false }),
      { ...getButtonVariant(true), hidden: (cmp) => !cmp?.content?.showButton },
      Textfield({ name: 'buttonLabel', label: 'Button label', hidden: (cmp) => !cmp?.content?.showButton }),
      { ...getButtonIcon(true), hidden: (cmp) => !cmp?.content?.showButton },
      ...getButtonAppearanceOptions(true).map((field) => ({ ...field, hidden: (cmp) => !cmp?.content?.showButton }))
    ),
    Section(
      'Appearance',
      Dropdown({
        name: 'titleVariant',
        label: 'Title Size',
        options: [
          { label: 'Large', value: 'titleLarge' },
          { label: 'Middle', value: 'titleMedium' },
          { label: 'Small', value: 'titleSmall' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'subtitleVariant',
        label: 'Subtitle Size',
        options: [
          { label: 'Large', value: 'titleLarge' },
          { label: 'Medium', value: 'titleMedium' },
          { label: 'Small', value: 'titleSmall' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'titleFontWeight',
        label: 'Title font weight',
        options: fontWeightOptions,
        filter: null,
      }),
      Dropdown({
        name: 'subtitleFontWeight',
        label: 'Subtitle font weight',
        options: fontWeightOptions,
        filter: null,
      })
    ),
  ],
  exposedStyles: [
    Color({ name: 'color', label: 'Text color' }),
    Number({
      name: 'borderRadius',
      label: 'Border radius (px)',
      min: 0,
      max: 16,
      tooltip: 'Leave empty to inherit from branding settings',
    }),
    Switch({
      name: 'showBorder',
      label: 'Show border',
      defaultChecked: false,
      tooltip: 'Uses the text color above as the border color',
    }),
  ],
  defaultContent: {
    content: {
      cardType: 'overlay',
      titleVariant: 'titleLarge',
      subtitleVariant: 'titleMedium',
      titleFontWeight: 'regular',
      subtitleFontWeight: 'regular',
    },
  },
})

export const list = Component('list', {
  label: 'List',
  fields: [
    Dropdown({
      name: 'listType',
      label: 'List Type',
      options: [
        { label: 'Product List', value: 'productList' },
        { label: 'List', value: 'screenList' },
      ],
      filter: null,
    }),
    Dropdown({
      label: 'Filter Property',
      name: 'filterProperty',
      hidden: (component) => component?.content?.listType !== 'productList',
      options: [{ label: 'Category', value: 'Category' }],
    }),
    Textfield({
      label: 'Filter value',
      name: 'filter',
      hidden: (component) => component?.content?.listType !== 'productList',
    }),
    Number({
      name: 'limit',
      label: 'Number of items to display',
    }),
    Dropdown({
      name: 'sort',
      label: 'Sort by',
      options: [
        { label: 'Name (A-Z)', value: 'alphabetical' },
        { label: 'Name (Z-A)', value: 'alphabetical-reverse' },
        { label: 'Price (low to high)', value: 'price-low-high' },
        { label: 'Price (high to low)', value: 'price-high-low' },
        { label: '--', value: 'none' },
      ],
      hidden: (component) => component?.content?.listType !== 'productList',
      filter: null,
    }),
    Dropdown({
      name: 'sort',
      label: 'Sort by',
      options: [
        { label: 'Name (A-Z)', value: 'alphabetical' },
        { label: 'Name (Z-A)', value: 'alphabetical-reverse' },
        { label: 'None', value: 'none' },
      ],
      hidden: (component) => component?.content?.listType !== 'screenList',
      filter: null,
    }),
    Screens({
      name: 'items',
      label: 'Screens',
      hidden: (component) => component?.content?.listType !== 'screenList',
      filter: null,
    }),
    Section(
      'Navigation',
      Switch({
        name: 'filterOnlyProductScreens',
        defaultChecked: false,
        label: 'Allow navigating to non-product screens',
        tooltip: 'If disabled, the list below will only allow you to choose screens containing product components.',
        hidden: (component) => component?.content?.listType !== 'productList',
      }),
      Screen({
        name: 'linkTo',
        label: 'Navigate to',
        hidden: (component) =>
          component?.content?.listType !== 'productList' || !component?.content?.filterOnlyProductScreens,
      }),
      Screen({
        name: 'linkTo',
        label: 'Navigate to',
        filter: filterOnlyScreensContainingProductComponents,
        hidden: (component) =>
          component?.content?.listType !== 'productList' || !!component?.content?.filterOnlyProductScreens,
      }),
      Textfield({
        name: 'searchParamDestinationPath',
        label: 'Destination Form prefill path',
        tooltip:
          'If the destination screen contains a form, you can specify the path to the field that should be prefilled.',
      }),
      Textfield({
        name: 'searchParamSearchValueSelector',
        label: 'Destination Form prefill selector',
        tooltip:
          'If the destination screen contains a form, you can specify the selector of the field that should be prefilled. For Product Lists a product object will be used as a base for the prefill, for a regular List a screen meta object will be used.',
      })
    ),

    Section(
      'Texts',
      Dropdown({
        name: 'textTransform',
        label: 'Text Transform',
        options: [
          { label: 'None', value: 'none' },
          { label: 'Uppercase', value: 'uppercase' },
          { label: 'Lowercase', value: 'lowercase' },
          { label: 'Capitalize', value: 'capitalize' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'titleSize',
        label: 'Title size',
        options: [
          { label: 'X-Small', value: 'labelSmall' },
          { label: 'Small', value: 'labelMedium' },
          { label: 'Medium', value: 'labelLarge' },
          { label: 'Large', value: 'titleMedium' },
          { label: 'X-Large', value: 'titleLarge' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'titleFontWeight',
        label: 'Title font weight',
        options: fontWeightOptions,
        filter: null,
      }),
      Switch({
        name: 'hideSubtitle',
        label: 'Hide subtitle',
        defaultChecked: false,
      }),
      Dropdown({
        name: 'subtitleSize',
        label: 'Subtitle size',
        hidden: (component) => !!component?.content?.hideSubtitle,
        options: [
          { label: 'X-Small', value: 'labelSmall' },
          { label: 'Small', value: 'labelMedium' },
          { label: 'Medium', value: 'labelLarge' },
          { label: 'Large', value: 'titleMedium' },
          { label: 'X-Large', value: 'titleLarge' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'subtitleFontWeight',
        label: 'Subtitle font weight',
        hidden: (component) => !!component?.content?.hideSubtitle,
        options: fontWeightOptions,
        filter: null,
      })
    ),
    Section(
      'Appearance',
      Dropdown({
        name: 'layout',
        label: 'Layout',
        options: [
          { label: 'Vertical/Grid', value: 'vertical/grid' },
          { label: 'Horizontal', value: 'horizontal' },
        ],
        filter: null,
      }),
      // In this dropdown, *layouts* array (if present) is used to white-list renderer options based on component *layout*
      Dropdown({
        name: 'itemRenderer',
        label: 'Item variant',
        options: [
          { label: 'Image and text in one line', value: 'image-text-one-line', layouts: ['vertical/grid'] },
          { label: 'Image and text overlay', value: 'overlay', layouts: ['horizontal', 'vertical/grid'] },
          { label: 'Image and text below', value: 'stacked', layouts: ['horizontal', 'vertical/grid'] },
          { label: 'Single Image', value: 'image', layouts: ['horizontal', 'vertical/grid'] },
        ],
        filter: (opt, cmp) => (opt.layouts ? opt.layouts.includes(cmp.content.layout) : true),
      }),
      Slider({
        name: 'itemsInRow',
        label: 'Items in row',
        min: 1,
        max: 12,
        hidden: (component) => component?.content?.layout !== 'vertical/grid',
      }),
      Number({
        name: 'imageWidth',
        label: 'Item width (px)',
        min: 0,
        max: 1000,
        hidden: (component) => component?.content?.layout !== 'horizontal',
      }),
      Number({
        name: 'itemHeight',
        label: 'Item height (px)',
        min: 0,
        max: 1000,
        hidden: (component) => component?.content?.layout !== 'horizontal',
      }),
      Number({
        name: 'itemRadius',
        label: 'Item radius (px)',
        min: 0,
        max: 16,
        tooltip: 'Leave empty to inherit from branding settings',
      }),
      imageResizeMode
    ),
  ],
  exposedStyles: [Number({ name: 'gap', label: 'Gap' }), Color({ name: 'color', label: 'Text color' })],
  defaultContent: {
    content: {
      layout: 'horizontal',
      itemRenderer: 'overlay',
      itemsInRow: 1,
      items: [],
      filterOnlyProductScreens: false,
      hideSubtitle: false,
      imageWidth: 120,
      imageResize: 'cover',
      filterProperty: 'Category',
      filter: '',
      limit: 5,
      sort: 'none',
      titleFontWeight: 'regular',
      subtitleFontWeight: 'light',
    },
  },
})

export const wishList = Component('wishList', {
  label: 'Wishlist',
  category: 'Cart & Checkout',
  description: 'Show a list of products in a wish list',
  fields: [
    Textfield({ name: 'wishListTitle', label: 'Wishlist Title' }),
    Textfield({ name: 'emptyWishListLabel', label: 'Empty Wishlist Label' }),
  ],
  exposedStyles: [Number({ name: 'gap', label: 'Gap' }), Color({ name: 'color', label: 'Text color' })],
  defaultContent: {
    content: {
      hideSubtitle: false,
    },
  },
})

export const panel = Component('panel', {
  label: 'Panel',
  category: 'Layout',
  description:
    'Serves as a container for other components. Has several options to visually highlight a group of components, eg. by additional padding',
  hasChildren: true,
  fields: [
    Dropdown({
      name: 'layout',
      label: 'Layout',
      options: [
        { label: 'Vertical', value: 'vertical' },
        { label: 'Horizontal', value: 'horizontal' },
      ],
      filter: null,
    }),
    Switch({ name: 'fullHeight', label: 'Full height (100%)', defaultChecked: false }),
    Switch({ name: 'showBorder', label: 'Show border', defaultChecked: true }),
    Switch({
      name: 'itemsFillHeight',
      label: 'Equally sized items',
      hidden: (cmp) => cmp?.meta?.layout !== 'vertical',
    }),
    Switch({
      name: 'itemsFillWidth',
      label: 'Equally sized items',
      hidden: (cmp) => cmp?.meta?.layout !== 'horizontal',
    }),
    Switch({
      name: 'wrapOverflowing',
      label: 'Wrap overflowing items',
      hidden: (cmp) => cmp?.meta?.layout !== 'horizontal' || !!cmp?.meta?.itemsFillWidth,
    }),
  ],
  exposedStyles: [
    Color({ name: 'backgroundColor', label: 'Background color' }),
    Number({ name: 'padding', label: 'Padding (px)', min: 0, max: 500 }),
    Number({ name: 'margin', label: 'Margin (px)', min: 0, max: 500 }),
    Number({ name: 'gap', label: 'Gap (px)', min: 0, max: 500 }),
    Number({ name: 'height', label: 'Height (px)', hidden: (cmp) => !!cmp?.meta?.fullHeight }),
    Color({ name: 'borderColor', label: 'Border color', hidden: (cmp) => !cmp?.meta?.showBorder }),
    Number({
      name: 'borderRadius',
      label: 'Border radius (px)',
      min: 0,
      max: 500,
      hidden: (cmp) => !cmp?.meta?.showBorder,
    }),
    Dropdown({
      name: 'alignItems',
      label: 'Items alignment',
      options: [
        { label: 'Left', value: 'flex-start' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'flex-end' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'justifyContent',
      label: 'Items distribution',
      options: [
        { label: 'Start', value: 'flex-start' },
        { label: 'Centered', value: 'center' },
        { label: 'Space between', value: 'space-between' },
        { label: 'End', value: 'flex-end' },
      ],
      filter: null,
    }),
  ],
  defaultContent: {
    meta: {
      showBorder: true,
      layout: 'horizontal',
      itemsFillWidth: true,
      wrapOverflowing: false,
    },
    styles: {
      gap: 8,
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: 8,
      padding: 8,
    },
    content: [],
  },
})
export const image = Component('image', {
  label: 'Image',
  description: 'Displays an image',
  fields: [
    Image({ name: 'image', label: 'Image' }),
    Switch({ name: 'fullWidth', label: 'Full Width', defaultChecked: true }),
    imageResizeMode,
    Screen({ name: 'linkTo', label: 'Link to', filter: null, allowProduct: true, allowExternal: true }),
  ],
  exposedStyles: [
    Number({ name: 'width', label: 'Width (px)', min: 0, max: 1000 }),
    Number({ name: 'height', label: 'Height (px)', min: 0, max: 1000 }),
  ],
  defaultContent: {
    content: {
      image: DEFAULT_THUMBNAIL,
    },
    styles: {},
  },
})
export const title = Component('title', {
  label: 'Title',
  category: 'Texts',
  description: 'Displays a title, giving control over text, size and font weight.',
  fields: [
    Textfield({ name: 'text', label: 'Text' }),
    Dropdown({
      name: 'size',
      label: 'Size',
      options: [
        { label: 'X Small', value: 'xsmall' },
        { label: 'Small', value: 'small' },
        { label: 'Medium', value: 'medium' },
        { label: 'Large', value: 'large' },
        { label: 'X Large', value: 'xlarge' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'weight',
      label: 'Weight',
      options: fontWeightOptions,
      filter: null,
    }),
    Dropdown({
      name: 'textAlignment',
      label: 'Text Alignment',
      options: alignmentOptions,
      filter: null,
    }),
  ],
  exposedStyles: [Color({ name: 'color', label: 'Text Color' })],
  defaultContent: {
    content: { text: 'Title', size: 'medium', weight: 'regular', textAlignment: 'left' },
  },
})
export const text = Component('text', {
  label: 'Text',
  category: 'Texts',
  description: 'Displays a piece of text, giving control over content, size and font weight.',
  fields: [Textfield({ name: 'text', label: 'Text' }), ...textProps],
  exposedStyles: [Color({ name: 'color', label: 'Text Color' })],
  defaultContent: {
    content: { text: 'Text', size: 'medium', weight: 'regular', textAlignment: 'left' },
  },
})

export const productProperty = Component('productProperty', {
  label: 'Product Property',
  category: 'Product',
  description: 'Displays a value of any provided product property',
  fields: [Textfield({ name: 'property', label: 'Property' }), Section('Appearance', ...textProps)],
  exposedStyles: [Color({ name: 'color', label: 'Text Color' })],
  defaultContent: {
    content: { size: 'medium', weight: 'regular', textAlignment: 'left' },
  },
})

export const productCard = Component('productCard', {
  label: 'Product Card',
  category: 'Product',
  defaultContent: {
    content: { product: null },
  },
  priorityInListing: true,
})
export const productTitle = Component('productTitle', {
  label: 'Product Title',
  category: 'Product',
  fields: [
    Dropdown({
      name: 'size',
      label: 'Size',
      options: [
        { label: 'X Small', value: 'xsmall' },
        { label: 'Small', value: 'small' },
        { label: 'Medium', value: 'medium' },
        { label: 'Large', value: 'large' },
        { label: 'X Large', value: 'xlarge' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'weight',
      label: 'Weight',
      options: fontWeightOptions,
      filter: null,
    }),
    Dropdown({
      name: 'textAlignment',
      label: 'Text Alignment',
      options: alignmentOptions,
      filter: null,
    }),
  ],
  defaultContent: {
    content: { size: 'medium', weight: 'regular', textAlignment: 'left' },
  },
  exposedStyles: [Color({ name: 'color', label: 'Text Color' })],
})
export const productPrice = Component('productPrice', {
  label: 'Product Price',
  category: 'Product',
  defaultContent: {
    content: {},
  },
  exposedStyles: [Color({ name: 'color', label: 'Text Color' })],
})
export const productVideo = Component('productVideo', {
  label: ' Product Video',
  category: 'Product',
  fields: [
    Switch({ name: 'autoplay', label: 'Autoplay', defaultChecked: true }),
    Switch({ name: 'loop', label: 'Play in loop', defaultChecked: true }),
    Switch({ name: 'muted', label: 'Mute the video', defaultChecked: true }),
    Switch({ name: 'controls', label: 'Show controls', defaultChecked: true }),
    imageResizeMode,
  ],
  exposedStyles: [Number({ name: 'height', label: 'Video height)', min: 0, max: 1000 })],
  defaultContent: {
    meta: { component: 'productVideo' },
    content: {
      autoplay: true,
    },
    styles: {},
  },
})
export const productImage = Component('productImage', {
  label: 'Product Image',
  category: 'Product',
  fields: [Switch({ name: 'fullWidth', label: 'Full Width', defaultChecked: true }), imageResizeMode],
  defaultContent: {
    content: {
      imageResize: 'cover',
      fullWidth: true,
    },
    styles: {
      height: 450,
    },
  },
  styles: {
    height: 450,
  },
  exposedStyles: [
    Number({ name: 'width', label: 'Width (px)', min: 0, max: 1000, filter: (cmp) => !cmp?.content?.fullWidth }),
    Number({ name: 'height', label: 'Height (px)', min: 0, max: 1000 }),
  ],
})
export const productAddToCartButton = Component('productAddToCartButton', {
  label: 'Product Add to Cart Button',
  category: 'Product',
  fields: [
    Dropdown({
      name: 'appearanceVariant',
      label: 'Content variant',
      options: [
        { label: 'Text & Icon', value: 'text-icon' },
        { label: 'Text', value: 'text' },
        { label: 'Icon', value: 'icon' },
      ],
      filter: null,
    }),
    Textfield({ name: 'label', label: 'Label', hidden: (cmp) => cmp?.content?.appearanceVariant === 'icon' }),
    getButtonVariant(),
    Section('Appearance', getAlignmentOptions(), ...getButtonAppearanceOptions()),
  ],
  defaultContent: {
    content: {
      alignment: 'stretch',
      label: 'Add to cart',
      appearanceVariant: 'text-icon',
    },
  },
})
export const productDescription = Component('productDescription', {
  label: 'Product Description',
  category: 'Product',
  defaultContent: {
    content: {},
  },
  exposedStyles: [Color({ name: 'color', label: 'Text Color' })],
})
export const productAddToWishListButton = Component('productAddToWishListButton', {
  label: 'Add to Wishlist Button',
  category: 'Product',
  fields: [
    Dropdown({
      name: 'appearanceVariant',
      label: 'Content variant',
      options: [
        { label: 'Text & Icon', value: 'text-icon' },
        { label: 'Text', value: 'text' },
        { label: 'Icon', value: 'icon' },
      ],
      filter: null,
    }),
    Textfield({ name: 'label', label: 'Label', hidden: (cmp) => cmp?.content?.appearanceVariant === 'icon' }),
    Textfield({ name: 'message', label: 'Message after click' }),
    getButtonVariant(),
    Section('Appearance', getAlignmentOptions(), ...getButtonAppearanceOptions()),
  ],
  defaultContent: {
    content: {
      alignment: 'stretch',
      label: 'Add to Wishlist',
      appearanceVariant: 'text-icon',
      message: 'Product added to Wishlist',
    },
  },
})

export const form = Component('form', {
  label: 'Form',
  category: 'Form',
  description:
    'A wrapper for form elements. Allows you to specify a form ID, event type and other elements related to form submissions. Remember that all form-related elements (such as Text Field, Dropdown etc.) should be inside a Form wrapper.',
  hasChildren: true,
  fields: [
    formId,
    Switch({
      name: 'ignoreEventSending',
      label: 'Ignore event sending',
      defaultChecked: false,
      hidden: (cmp) => !cmp?.meta?.formId,
      tooltip:
        '(For multi-step forms) If enabled, the form will not send an event on submit. This can be useful if you want the event sent only on the last step with all the data from all steps.',
    }),
    Switch({
      name: 'overwriteFormDataOnSubmit',
      label: 'Overwrite form data on submit',
      defaultChecked: false,
      hidden: (cmp) => !cmp?.meta?.formId,
      tooltip:
        '(For multi-step forms) If enabled, the form will overwrite the current form data for the given form ID. This can be useful if you want to reset the form data for the form ID on submission of this step. (e.g. when filling out the form again after submission and wanting to send partial data on each step)',
    }),
    Textfield({
      name: 'eventType',
      label: 'Event type',
      hidden: (cmp) => cmp?.meta?.ignoreEventSending && cmp?.meta?.formId,
    }),
    Textfield({
      name: 'webhookUrl',
      label: 'Webhook URL',
      hidden: (cmp) => cmp?.meta?.ignoreEventSending && cmp?.meta?.formId,
    }),
    Screen({ name: 'link', label: 'Navigate on submit', filter: null }),
    Textfield({ name: 'toastMessage', label: 'Toast message' }),
    Switch({ name: 'hideResetButton', label: 'Hide reset button', defaultChecked: false }),
    Textfield({ name: 'submitButtonLabel', label: 'Submit button label' }),
    Textfield({ name: 'resetButtonLabel', label: 'Reset button label', hidden: (cmp) => !!cmp?.meta?.hideResetButton }),
  ],
  defaultContent: {
    meta: {
      eventType: '',
      webhookUrl: '',
      toastMessage: '',
      hideResetButton: false,
      submitButtonLabel: 'Submit',
      resetButtonLabel: 'Reset',
      ignoreEventSending: false,
      overwriteFormDataOnSubmit: false,
    },
    content: [],
  },
  priorityInListing: true,
})

export const textField = Component('textField', {
  label: 'Text Field',
  description: 'Remember that all form-related elements should be inside a Form wrapper.',
  category: 'Form',
  fields: [
    Textfield({ name: 'label', label: 'Label' }),
    Textfield({ name: 'placeholder', label: 'Placeholder' }),
    Dropdown({
      name: 'inputMode',
      label: 'Input Mode',
      options: [
        { label: 'None', value: 'none' },
        { label: 'Text', value: 'text' },
        { label: 'Decimal', value: 'decimal' },
        { label: 'Numeric', value: 'numeric' },
        { label: 'Phone number', value: 'tel' },
        { label: 'Search', value: 'search' },
        { label: 'Email', value: 'email' },
        { label: 'URL', value: 'url' },
      ],
    }),
    Section(
      'Data handling',
      Textfield({ name: 'path', label: 'Path' }),
      Textfield({ name: 'defaultValue', label: 'Default Value' }),
      Switch({ name: 'prefill', label: 'Prefill', defaultChecked: true })
    ),
    Section(
      'Appearance',
      appearanceMode,
      Switch({ name: 'multiline', label: 'Multiline', defaultChecked: false }),
      Switch({
        name: 'isPassword',
        label: 'Password',
        defaultChecked: false,
        hidden: (cmp) => !!cmp?.content?.multiline,
      }),
      Switch({ name: 'autoCorrect', label: 'Auto Correct', defaultChecked: false }),
      Dropdown({
        name: 'enterKeyHint',
        label: 'Keyboard return key text',
        options: [
          { label: 'Done', value: 'done' },
          { label: 'Enter', value: 'enter' },
          { label: 'Go', value: 'go' },
          { label: 'Next', value: 'next' },
          { label: 'Previous', value: 'previous' },
          { label: 'Search', value: 'search' },
          { label: 'Send', value: 'send' },
        ],
        tooltip: 'Determines what text should be shown at the the return key of the device keyboard.',
      })
    ),
    Section(
      'Icons and affixes',
      Radio({
        name: 'leftElement',
        label: 'Left element',
        options: [
          { label: 'None', value: 'off' },
          { label: 'Icon', value: 'icon' },
          { label: 'Affix', value: 'affix' },
        ],
      }),
      Icon({
        name: 'leftIcon',
        label: 'Left Icon',
        hidden: (cmp) => cmp?.content?.leftElement !== 'icon',
      }),
      Textfield({
        name: 'leftAffix',
        label: 'Left Affix',
        hidden: (cmp) => cmp?.content?.leftElement !== 'affix',
      }),
      Radio({
        name: 'rightElement',
        label: 'Right element',
        options: [
          { label: 'None', value: 'off' },
          { label: 'Icon', value: 'icon' },
          { label: 'Affix', value: 'affix' },
        ],
      }),
      Icon({
        name: 'rightIcon',
        label: 'Right Icon',
        hidden: (cmp) => cmp?.content?.rightElement !== 'icon',
      }),
      Textfield({
        name: 'rightAffix',
        label: 'Right Affix',
        hidden: (cmp) => cmp?.content?.rightElement !== 'affix',
      })
    ),
  ],
  defaultContent: {
    content: {
      path: '',
      label: '',
      mode: 'outlined',
      prefill: true,
      multiline: false,
      autoCorrect: false,
      isPassword: false,
      leftElement: 'off',
      rightElement: 'off',
      enterKeyHint: 'enter',
    },
  },
})

export const checkboxGroup = Component('checkboxGroup', {
  label: 'Checkbox Group',
  description: 'Remember that all form-related elements should be inside a Form wrapper.',
  category: 'Form',
  fields: [
    OptionEditor({
      name: 'options',
      label: 'Options',
      columns: [
        {
          name: 'path',
          label: 'Path',
        },
        { name: 'label', label: 'Label' },
        {
          name: 'uncheckedValue',
          label: 'Unchecked value',
          tooltip: 'Value to be sent when the checkbox/switch is unchecked. If left empty, false will be sent',
        },
        {
          name: 'checkedValue',
          label: 'Checked value',
          tooltip: 'Value to be sent when the checkbox/switch is checked. If left empty, true will be sent',
        },
      ],
    }),
    Switch({ name: 'prefill', label: 'Prefill', defaultChecked: true }),
    Dropdown({
      name: 'variant',
      label: 'Variant',
      options: [
        { label: 'Checkbox', value: 'checkbox' },
        { label: 'Switch', value: 'switch' },
      ],
    }),
  ],
  exposedStyles: [
    Number({ name: 'gap', label: 'Gap (px)', min: 0, max: 100 }),
    Dropdown({
      name: 'flexDirection',
      label: 'Direction',
      options: [
        { label: 'Vertical', value: 'vertical' },
        { label: 'Horizontal', value: 'horizontal' },
      ],
    }),
  ],
  defaultContent: {
    content: {
      options: [],
      prefill: true,
      variant: 'checkbox',
    },
    styles: {
      flexDirection: 'vertical',
    },
  },
})

export const dropdown = Component('dropdown', {
  label: 'Dropdown',
  description: 'Remember that all form-related elements should be inside a Form wrapper.',
  category: 'Form',
  fields: [
    Textfield({ name: 'label', label: 'Label' }),
    Section(
      'Data handling',
      Textfield({ name: 'path', label: 'Path' }),
      OptionEditor({
        name: 'options',
        label: 'Options',
        columns: [
          { name: 'label', label: 'Label' },
          { name: 'value', label: 'Value' },
        ],
      }),
      Switch({ name: 'prefill', label: 'Prefill', defaultChecked: true })
    ),
    Section(
      'Appearance',
      appearanceMode,
      OptionEditor({
        name: 'menuContentStyle',
        label: 'Menu content styles',
        saveAs: 'object',
        columns: [
          { name: 'label', label: 'Label' },
          { name: 'value', label: 'Value' },
        ],
      })
    ),
  ],
  defaultContent: {
    content: {
      path: '',
      label: '',
      options: [],
      mode: 'outlined',
      prefill: true,
    },
  },
})

export const radioGroup = Component('radioGroup', {
  label: 'Radio Group',
  description: 'Remember that all form-related elements should be inside a Form wrapper.',
  category: 'Form',
  fields: [
    Textfield({ name: 'path', label: 'Path' }),
    OptionEditor({
      name: 'options',
      label: 'Options',
      columns: [
        { name: 'label', label: 'Label' },
        { name: 'value', label: 'Value' },
      ],
    }),
    Switch({ name: 'prefill', label: 'Prefill', defaultChecked: true }),
  ],
  defaultContent: {
    content: {
      path: '',
      options: [],
      prefill: true,
    },
  },
})

export const userLogin = Component('userLogin', {
  label: 'User Login',
  category: 'Profile',
  fields: [
    Textfield({ name: 'title', label: 'Title' }),
    Textfield({ name: 'usernameLabel', label: 'Username Label' }),
    Textfield({ name: 'passwordLabel', label: 'Password Label' }),
  ],
  defaultContent: {
    content: {
      title: 'Login',
      usernameLabel: 'Email',
      passwordLabel: 'Password',
    },
  },
})

export const userDetails = Component('userDetails', {
  label: 'User Details',
  category: 'Profile',
  fields: [Switch({ name: 'showLogoutButton', label: 'Show logout button' })],
  defaultContent: {
    content: {
      showLogoutButton: true,
    },
  },
})

export const htmlText = Component('htmlText', {
  label: 'Rich Text',
  category: 'Texts',
  description: 'Displays an arbitrary HTML. Use with caution!',
  fields: [RichText({ name: 'text', label: 'Content' })],
  defaultContent: {
    content: {
      text: 'Placeholder for a Rich Text',
    },
  },
})

export const divider = Component('divider', {
  label: 'Divider',
  category: 'Layout',
  description: 'Displays a horizontal line to separate content.',
  fields: [
    Dropdown({
      name: 'variant',
      label: 'Variant',
      options: [
        { label: 'Normal', value: 'normal' },
        { label: 'Bold', value: 'bold' },
        { label: 'Bolder', value: 'bolder' },
      ],
    }),
    Switch({
      name: 'horizontalInset',
      label: 'Horizontal Inset',
      defaultChecked: false,
    }),
  ],
  exposedStyles: [
    Number({ name: 'marginTop', label: 'Top margin (px)', min: 0, max: 500 }),
    Number({ name: 'marginBottom', label: 'Bottom margin (px)', min: 0, max: 500 }),
  ],
  defaultContent: {
    content: {
      variant: 'normal',
      horizontalInset: false,
    },
  },
})

export const cartSummary = Component('cartSummary', {
  label: 'Cart Summary',
  description: 'Displays list of products added in the cart',
  category: 'Cart & Checkout',
  fields: [
    Textfield({
      name: 'emptyCartMessage',
      label: 'Empty Cart Message',
    }),
    Section(
      'Promo Code',
      Switch({
        name: 'showPromoCode',
        label: 'Show Promo Code',
        defaultChecked: true,
      }),
      OptionEditor({
        name: 'discountCodes',
        label: 'Discount Codes',
        columns: [
          {
            name: 'label',
          },
          { name: 'value', label: 'Value %' },
        ],
        hidden: (component) => component?.content?.showPromoCode !== true,
      }),
      Textfield({
        name: 'promoMessage',
        label: 'Promo code success message',
        hidden: (component) => component?.content?.showPromoCode !== true,
      }),
      Textfield({
        name: 'promoErrorMessage',
        label: 'Promo code error message',
        hidden: (component) => component?.content?.showPromoCode !== true,
      })
    ),
    Section(
      'Price/Discount',
      Textfield({ name: 'totalLabel', label: 'Total Price Label' }),
      Textfield({
        name: 'subtotalLabel',
        label: 'Subtotal Price Label',
      }),
      Switch({ name: 'showDiscount', label: 'Show Discount', defaultChecked: true }),
      Textfield({
        name: 'discountLabel',
        label: 'Discount Label',
        hidden: (component) => component?.content?.showDiscount !== true,
      }),
      Switch({ name: 'showShippingCharge', label: 'Show Shipping Price', defaultChecked: true }),
      Textfield({
        name: 'shippingLabel',
        label: 'Shipping Price Label',
        hidden: (component) => component?.content?.showShippingCharge !== true,
      })
    ),
  ],
  defaultContent: {
    content: {
      promoMessage: 'Promo code applied successfully',
      promoErrorMessage: 'Invalid Promo Code',
      totalLabel: 'TOTAL',
      subtotalLabel: 'SUBTOTAL',
      shippingLabel: 'SHIPPING',
      discountLabel: 'DISCOUNT',
      showDiscount: true,
      showPromoCode: false,
      showShippingCharge: true,
      discountCodes: [],
    },
  },
})
export const video = Component('video', {
  label: 'Video',
  description: 'Video',
  hasChildren: false,
  fields: [
    Video({ name: 'url', label: 'Video URL' }),
    Image({ name: 'videoPoster', label: 'Video Poster' }),
    Switch({ name: 'autoplay', label: 'Autoplay', defaultChecked: true }),
    Switch({ name: 'loop', label: 'Play in loop', defaultChecked: true }),
    Switch({ name: 'muted', label: 'Mute the video', defaultChecked: true }),
    Switch({ name: 'controls', label: 'Show controls', defaultChecked: true }),
    imageResizeMode,
  ],
  exposedStyles: [
    Color({ name: 'color', label: 'Text color' }),
    Number({ name: 'height', label: 'Video height)', min: 0, max: 1000 }),
  ],
  defaultContent: {
    meta: { component: 'video' },
    content: {
      autoplay: true,
    },
    styles: {
      fontSize: 16,
    },
  },
})

export const map = Component('map', {
  label: 'Map',
  description: 'Display a map',
  hasChildren: false,
  fields: [
    Dropdown({
      name: 'displayAs',
      label: 'Display As',
      options: [
        { label: 'Full Map Component', value: 'fullMap' },
        { label: 'Button (Show map on click)', value: 'modal' },
      ],
      filter: null,
    }),
    { ...getButtonVariant(true), hidden: (cmp) => !cmp?.content?.modal },
    Textfield({ name: 'buttonLabel', label: 'Button label', hidden: (cmp) => !cmp?.content?.modal }),
    Section(
      'POI ',
      OptionEditor({
        name: 'mapCoordinates',
        label: 'Coordinates',
        columns: [
          { label: 'Label', name: 'label' },
          { label: 'Latitude', name: 'latitude' },
          { label: 'Longitude', name: 'longitude' },
          { label: 'Screen Open OnClick', name: 'link' },
        ],
      }),
      Textfield({ name: 'text', label: 'POI Name' })
    ),
  ],
  exposedStyles: [
    Number({ name: 'height', label: 'Height (px)', min: 0, max: 1000 }),
    Color({ name: 'color', label: 'Coordinates pin color' }),
    Color({ name: 'locationColor', label: 'Simulated Location pin color' }),
  ],

  defaultContent: {
    meta: { component: 'map' },
    content: {
      displayAs: 'fullMap',
    },
    styles: {
      height: 500,
      color: 'blue',
      locationColor: 'green',
    },
  },
})

export const datePicker = Component('datePicker', {
  label: 'Date Picker',
  category: 'Form',
  description:
    'Allows you to pick a date or a range of dates. Remember that all form-related elements should be inside a Form wrapper.',
  hasChildren: false,
  fields: [
    Dropdown({
      name: 'type',
      label: 'Type',
      options: [
        { label: 'Single', value: 'single' },
        { label: 'Double', value: 'double' },
        { label: 'Range', value: 'range' },
      ],
      filter: null,
    }),
    Textfield({ name: 'label', label: 'Label', hidden: (component) => component?.content?.type === 'double' }),
    Textfield({
      name: 'labelStart',
      label: 'Label (start)',
      hidden: (component) => component?.content?.type !== 'double',
    }),
    Textfield({ name: 'labelEnd', label: 'Label (end)', hidden: (component) => component?.content?.type !== 'double' }),
    Section(
      'Data handling',
      Textfield({ name: 'path', label: 'Path', hidden: (component) => component?.content?.type !== 'single' }),
      Textfield({
        name: 'pathStart',
        label: 'Path (start)',
        hidden: (component) => component?.content?.type === 'single',
      }),
      Textfield({ name: 'pathEnd', label: 'Path (end)', hidden: (component) => component?.content?.type === 'single' }),
      Textfield({
        name: 'defaultValue',
        label: 'Default Value',
        tooltip: 'ISO String date format',
        hidden: (component) => component?.content?.type !== 'single',
      }),
      Textfield({
        name: 'defaultValueStart',
        label: 'Default Value (start)',
        tooltip: 'ISO String date format',
        hidden: (component) => component?.content?.type === 'single',
      }),
      Textfield({
        name: 'defaultValueEnd',
        label: 'Default Value (end)',
        tooltip: 'ISO String date format',
        hidden: (component) => component?.content?.type === 'single',
      }),
      Switch({ name: 'prefill', label: 'Prefill', defaultChecked: true })
    ),
    Section(
      'Appearance',
      appearanceMode,
      Switch({ name: 'withDateFormatInLabel', label: 'With date format in label', defaultChecked: false })
    ),
  ],
  defaultContent: {
    content: {
      type: 'single',
      mode: 'outlined',
      path: '',
      label: '',
      prefill: true,
      withDateFormatInLabel: false,
    },
  },
})

export const timePicker = Component('timePicker', {
  label: 'Time Picker',
  category: 'Form',
  description: 'Remember that all form-related elements should be inside a Form wrapper.',
  hasChildren: false,
  fields: [
    Textfield({ name: 'label', label: 'Label' }),
    Section(
      'Data handling',
      Textfield({ name: 'path', label: 'Path' }),
      Switch({ name: 'prefill', label: 'Prefill', defaultChecked: true }),
      Textfield({ name: 'defaultValue', label: 'Default Value', tooltip: 'HH:mm format' })
    ),
    Section(
      'Appearance',
      appearanceMode,
      Switch({ name: 'withDateFormatInLabel', label: 'With time format in label', defaultChecked: false })
    ),
  ],
  defaultContent: {
    content: {
      mode: 'outlined',
      path: '',
      label: '',
      prefill: true,
      withDateFormatInLabel: false,
    },
  },
})

export const dateTimePicker = Component('dateTimePicker', {
  label: 'Date Time Picker',
  category: 'Form',
  description: 'Remember that all form-related elements should be inside a Form wrapper.',
  hasChildren: false,
  fields: [
    Textfield({ name: 'dateLabel', label: 'Date Label' }),
    Textfield({ name: 'timeLabel', label: 'Time Label' }),
    Section(
      'Data handling',
      Textfield({ name: 'path', label: 'Path' }),
      Switch({ name: 'prefill', label: 'Prefill', defaultChecked: true }),
      Textfield({ name: 'defaultValue', label: 'Default Value', tooltip: 'ISO String datetime format' })
    ),
    Section(
      'Appearance',
      appearanceMode,
      Switch({ name: 'withDateFormatInLabel', label: 'With date format in label', defaultChecked: false }),
      Switch({ name: 'withTimeFormatInLabel', label: 'With time format in label', defaultChecked: false })
    ),
  ],
  defaultContent: {
    content: {
      mode: 'outlined',
      path: '',
      dateLabel: '',
      timeLabel: '',
      prefill: true,
      withDateFormatInLabel: false,
      withTimeFormatInLabel: false,
    },
  },
})

export const timeSlotPicker = Component('timeSlotPicker', {
  label: 'Time Slot Picker',
  category: 'Form',
  description:
    'Acts as an appointment scheduler, allowing users to select from a pre-defined set of time slots. Remember that all form-related elements should be inside a Form wrapper.',
  hasChildren: false,
  fields: [
    Section(
      'Options',
      Number({ name: 'numberOfDays', label: 'Displayed days', min: 1, max: 7 }),
      MultiField({ name: 'timeOptions', label: 'Daily time options' })
    ),
    Section(
      'Data handling',
      Textfield({ name: 'path', label: 'Path' }),
      Switch({ name: 'prefill', label: 'Prefill', defaultChecked: true }),
      Textfield({ name: 'defaultValue', label: 'Default Value', tooltip: 'ISO String datetime format' })
    ),
    Section(
      'Appearance',
      Number({ name: 'numberOfTimeColumns', label: 'Number of columns for time options', min: 1, max: 4 })
    ),
  ],
  exposedStyles: [Number({ name: 'padding', label: 'Padding' }), Number({ name: 'gap', label: 'Gap' })],
  defaultContent: {
    content: {
      path: '',
      prefill: true,
      defaultValue: '',
      numberOfDays: 3,
      numberOfTimeColumns: 3,
      timeOptions: ['09:00', '10:00', '11:00', '12:00'],
    },
    styles: {
      gap: 16,
    },
  },
})

export const offer = Component('offer', {
  label: 'Experience Decisioning',
  category: 'AJO',
  description: 'Displays an offer from Code Based Experience',
  hasChildren: false,
  fields: [
    Textfield({ name: 'surface', label: 'Surface' }),
    Textfield({ name: 'fallbackImage', label: 'Fallback Image' }),
    Screen({
      name: 'linkTo',
      label: 'Link to',
      filter: null,
      tooltip: 'Link to be used in case of fallback image',
      allowExternal: true,
    }),
    Switch({ name: 'showTitle', label: 'Show Title', defaultChecked: false }),
    Switch({
      name: 'showDebugMessage',
      label: 'Show Debug Message',
      defaultChecked: false,
      tooltip: 'Shows the decisioning error message',
    }),
    Section(
      'Appearance',
      Slider({
        name: 'opacity',
        min: 0,
        max: 1,
        step: 0.01,
        displayAs: 'percentage',
        label: 'Overlay opacity',
        tooltip: 'Adjusting this value can help you provide better contrast between banner texts and the image',
      }),
      Dropdown({
        name: 'titleVariant',
        label: 'Title Size',
        options: [
          { label: 'Large', value: 'titleLarge' },
          { label: 'Middle', value: 'titleMedium' },
          { label: 'Small', value: 'titleSmall' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'subtitleVariant',
        label: 'Subtitle Size',
        options: [
          { label: 'Large', value: 'titleLarge' },
          { label: 'Medium', value: 'titleMedium' },
          { label: 'Small', value: 'titleSmall' },
        ],
        filter: null,
      }),
      imageResizeMode
    ),
    Section(
      'Alignment',
      Dropdown({
        name: 'horizontalAlignment',
        label: 'Horizontal Alignment',
        options: alignmentOptions,
        filter: null,
      }),
      Dropdown({
        name: 'contentAlignment',
        label: 'Content Alignment',
        options: alignmentOptions,
        filter: null,
      }),
      Dropdown({
        name: 'verticalAlignment',
        label: 'Vertical Alignment',
        options: [
          { label: 'Top', value: 'top' },
          { label: 'Middle', value: 'middle' },
          { label: 'Bottom', value: 'bottom' },
        ],
        filter: null,
      })
    ),
    Section(
      'Button',
      Switch({ name: 'showButton', label: 'Show button', defaultChecked: true }),
      { ...getButtonVariant(true), hidden: (cmp) => !cmp?.content?.showButton },
      Textfield({ name: 'buttonLabel', label: 'Button label', hidden: (cmp) => !cmp?.content?.showButton }),
      { ...getButtonIcon(true), hidden: (cmp) => !cmp?.content?.showButton },
      ...getButtonAppearanceOptions(true).map((field) => ({ ...field, hidden: (cmp) => !cmp?.content?.showButton }))
    ),
  ],
  exposedStyles: [
    Number({ name: 'height', label: 'Height (px)', min: 0, max: 1000 }),
    Color({ name: 'color', label: 'Text color' }),
  ],
  defaultContent: {
    titleVariant: 'titleLarge',
    subtitleVariant: 'titleMedium',
    horizontalAlignment: 'center',
    verticalAlignment: 'middle',
    contentAlignment: 'center',
    imageResize: 'cover',
    showTitle: false,
    showButton: false,
  },
  styles: {
    height: 200,
    color: 'white',
  },
})
export const offerRenderer = Component('offerRenderer', {
  label: 'Offer Decisioning',
  category: 'AJO',
  description: 'Displays an offer from Offer Decisioning based on provided scope.',
  hasChildren: false,
  fields: [
    Textarea({ name: 'decisionScope', label: 'Decision Scope' }),
    Textfield({ name: 'fallbackImage', label: 'Fallback Image' }),
    Screen({
      name: 'linkTo',
      label: 'Link to',
      filter: null,
      tooltip: 'Link to be used in case of fallback image',
      allowExternal: true,
    }),
    Switch({ name: 'showTitle', label: 'Show Title', defaultChecked: false }),
    Switch({
      name: 'showDebugMessage',
      label: 'Show Debug Message',
      defaultChecked: false,
      tooltip: 'Shows the decisioning error message',
    }),
    Section(
      'Appearance',
      Slider({
        name: 'opacity',
        min: 0,
        max: 1,
        step: 0.01,
        displayAs: 'percentage',
        label: 'Overlay opacity',
        tooltip: 'Adjusting this value can help you provide better contrast between banner texts and the image',
      }),
      Dropdown({
        name: 'titleVariant',
        label: 'Title Size',
        options: [
          { label: 'Large', value: 'titleLarge' },
          { label: 'Middle', value: 'titleMedium' },
          { label: 'Small', value: 'titleSmall' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'subtitleVariant',
        label: 'Subtitle Size',
        options: [
          { label: 'Large', value: 'titleLarge' },
          { label: 'Medium', value: 'titleMedium' },
          { label: 'Small', value: 'titleSmall' },
        ],
        filter: null,
      }),
      imageResizeMode
    ),
    Section(
      'Alignment',
      Dropdown({
        name: 'horizontalAlignment',
        label: 'Horizontal Alignment',
        options: alignmentOptions,
        filter: null,
      }),
      Dropdown({
        name: 'contentAlignment',
        label: 'Content Alignment',
        options: alignmentOptions,
        filter: null,
      }),
      Dropdown({
        name: 'verticalAlignment',
        label: 'Vertical Alignment',
        options: [
          { label: 'Top', value: 'top' },
          { label: 'Middle', value: 'middle' },
          { label: 'Bottom', value: 'bottom' },
        ],
        filter: null,
      })
    ),
    Section(
      'Button',
      Switch({ name: 'showButton', label: 'Show button', defaultChecked: true }),
      { ...getButtonVariant(true), hidden: (cmp) => !cmp?.content?.showButton },
      Textfield({ name: 'buttonLabel', label: 'Button label', hidden: (cmp) => !cmp?.content?.showButton }),
      { ...getButtonIcon(true), hidden: (cmp) => !cmp?.content?.showButton },
      ...getButtonAppearanceOptions(true).map((field) => ({ ...field, hidden: (cmp) => !cmp?.content?.showButton }))
    ),
  ],
  exposedStyles: [
    Number({ name: 'height', label: 'Height (px)', min: 0, max: 1000 }),
    Color({ name: 'color', label: 'Text color' }),
  ],
  defaultContent: {
    titleVariant: 'titleLarge',
    subtitleVariant: 'titleMedium',
    horizontalAlignment: 'center',
    verticalAlignment: 'middle',
    contentAlignment: 'center',
    imageResize: 'cover',
    showTitle: false,
    showButton: false,
  },
  styles: {
    height: 200,
    color: 'white',
  },
})

export const webView = Component('webView', {
  label: 'WebView',
  description: 'Open URL in web view',
  hasChildren: false,
  fields: [Textfield({ name: 'url', label: 'URL' })],
  defaultContent: {
    meta: { component: 'webView' },
    content: {
      url: 'https://www.adobe.com',
    },
  },
})
export const contentCards = Component('contentCards', {
  label: 'Content Cards',
  category: 'AJO',
  description: 'Display a Content Card',
  hasChildren: false,
  fields: [Textfield({ name: 'surface', label: 'Surface' })],
  exposedStyles: [
    Number({
      name: 'borderRadius',
      label: 'Border Radius (px)',
      min: 0,
      max: 16,
      tooltip: 'Leave empty to inherit from branding settings',
    }),
    Color({ name: 'color', label: 'Text color' }),
    Color({ name: 'backgroundColor', label: 'Background color' }),
    Color({ name: 'borderColor', label: 'Border color' }),
    Color({ name: 'buttonTextColor', label: 'Button Text color' }),
    Color({ name: 'buttonBackgroundColor', label: 'Button background color' }),
    Color({ name: 'buttonBorderColor', label: 'Button border color' }),
  ],
  defaultContent: {
    meta: { component: 'contentCards' },
    content: {
      surface: '',
    },
    styles: {
      borderColor: 'lightgrey',
      borderRadius: 9,
      backgroundColor: 'white',
    },
  },
})

export const inbox = Component('inbox', {
  label: 'Inbox',
  category: 'AJO',
  description: 'Display an inbox',
  hasChildren: false,
  fields: [Textfield({ name: 'surface', label: 'Surface' })],
  exposedStyles: [
    Number({
      name: 'borderRadius',
      label: 'Border Radius (px)',
      min: 0,
      max: 16,
      tooltip: 'Leave empty to inherit from branding settings',
    }),
    Color({ name: 'color', label: 'Text color' }),
    Color({ name: 'backgroundColor', label: 'Background color' }),
    Color({ name: 'borderColor', label: 'Border color' }),
    Color({ name: 'buttonTextColor', label: 'Button Text color' }),
    Color({ name: 'buttonBackgroundColor', label: 'Button background color' }),
    Color({ name: 'buttonBorderColor', label: 'Button border color' }),
  ],
  defaultContent: {
    meta: { component: 'inbox' },
    content: {
      surface: '',
    },
    styles: {
      borderColor: 'lightgrey',
      borderRadius: 9,
      backgroundColor: 'white',
    },
  },
})

export const flightSearch = {
  name: 'flightSearch',
  label: 'Flight Search',
  category: '[Vertical] WKND Fly Components',
  description:
    'Displays a flight search component with *from* and *to* fields as well as date and additional options. Make sure to use the same form ID as the one used in the flight results component.',
  fields: [
    Dropdown({
      name: 'type',
      label: 'Input type',
      options: [
        { label: 'Picker (from products)', value: 'automaticPicker' },
        { label: 'Picker (user-defined)', value: 'picker' },
        { label: 'Text field', value: '' },
      ],
      filter: null,
    }),
    OptionEditor({
      name: 'locations',
      label: 'Airport locations',
      columns: [
        { name: 'label', label: 'Label' },
        { name: 'value', label: 'Value' },
      ],
      saveAs: 'array',
      hidden: (cmp) => cmp.content.type !== 'picker',
    }),
    Screen({
      name: 'link',
      label: 'Navigate to',
      filter: null,
      tooltip:
        'Choose a screen with your corresponding Flight Results component (or leave blank if your Flight Results component is on the same screen)',
    }),
    OptionEditor({
      name: 'options',
      label: 'Additional Options',
      columns: [
        { label: 'Label', name: 'label', type: 'text' },
        { label: 'Path', name: 'path', type: 'text' },
      ],
    }),
    Section(
      'Data Handling',
      Textfield({
        name: 'formId',
        label: 'Flight Form ID',
        tooltip: 'Required. Should match the form ID used in the Flight Results component.',
      }),
      Textfield({ name: 'eventType', label: 'Event Type' }),
      Textfield({ name: 'fromDefaultValue', label: 'Default Value (from)' }),
      Textfield({ name: 'toDefaultValue', label: 'Default Value (to)' }),
      Textfield({ name: 'dateToDefaultValue', label: 'Default Value (date - flight there)' }),
      Textfield({ name: 'dateBackDefaultValue', label: 'Default Value (date - flight back)' })
    ),
    Section(
      'Appearance & labels',
      appearanceMode,
      Textfield({ name: 'fromLabel', label: 'From Label' }),
      Textfield({ name: 'toLabel', label: 'To Label' })
    ),
  ],
  defaultContent: {
    meta: {
      component: 'flightSearch',
    },
    content: {
      type: 'automaticPicker',
      options: [
        { label: 'Business class', path: 'flight.options.businessClass' },
        { label: 'Direct flights only', path: 'flight.options.directFlightsOnly' },
      ],
      formId: 'flights',
      eventType: 'flight.search',
      fromDefaultValue: '',
      toDefaultValue: '',
      fromLabel: 'From',
      toLabel: 'To',
      mode: 'outlined',
      prefill: true,
    },
  },
}

export const flightResults = {
  name: 'flightResults',
  label: 'Flight Results',
  category: '[Vertical] WKND Fly Components',
  description:
    'Displays flights that match Flight Search component input. Make sure to use the same form ID as the one used in the flight search component.',
  fields: [
    Screen({ name: 'link', label: 'Link', filter: null }),
    Textfield({
      name: 'from',
      label: 'From (optional)',
      tooltip:
        'Leave blank to read this value from the corresponding Flight Search component. Filling it will force this value to be used instead of the one from the Flight Search component.',
    }),
    Textfield({
      name: 'to',
      label: 'To (optional)',
      tooltip:
        'Leave blank to read this value from the corresponding Flight Search component. Filling it will force this value to be used instead of the one from the Flight Search component.',
    }),
    Section(
      'Data Handling',
      Textfield({
        name: 'flightSearchFormId',
        label: 'Flight Form ID',
        tooltip: 'Should match the form ID used in the Flight Search component.',
      }),
      Textfield({ name: 'eventType', label: 'Event Type' })
    ),
    Section(
      'Labels',
      Textfield({ name: 'emptyOptionLabel', label: 'Unavailable option label' }),
      Textfield({ name: 'noResultsLabel', label: 'No result text' })
    ),
  ],
  exposedStyles: [
    Number({
      name: 'borderRadius',
      label: 'Border Radius (px)',
      min: 0,
      max: 16,
      tooltip: 'Leave empty to inherit from branding settings',
    }),
  ],
  defaultContent: {
    meta: {
      component: 'flightResults',
    },
    content: {
      eventType: 'flight.select',
      emptyOptionLabel: 'Not available',
      noResultsLabel: "We're sorry. We didn't find any flights matching your criteria.",
      flightSearchFormId: 'flights',
    },
  },
}

export const search = Component('search', {
  label: 'Search Field',
  description:
    'You can use search field to navigate to a specific page and automaticlaly fill some form field with the data from the search',
  hasChildren: false,
  fields: [
    Textfield({ name: 'label', label: 'Label' }),
    Textfield({ name: 'placeholder', label: 'Placeholder' }),
    Screen({ name: 'link', label: 'Link to', filter: null, allowExternal: false, allowProduct: true }),
    Textfield({
      name: 'destinationPath',
      label: 'Destination Path',
      tooltip: 'Path of the form field from the destination page, to be filled with the search query',
    }),
    Textfield({ name: 'eventType', label: 'Event type' }),
    Section('Appearance', appearanceMode, getButtonVariant(true)),
  ],
  defaultContent: {
    meta: { component: 'search' },
    content: {
      label: '',
      placeholder: 'Search',
      destinationPath: '',
      eventType: '',
      mode: 'outlined',
      buttonVariant: 'text',
    },
  },
})

export const DEFAULT_COMPONENT = { ...banner.defaultContent, meta: { component: 'banner' } }

// Helper functions

function filterOnlyScreensContainingProductComponents(screen) {
  if (!Array.isArray(screen?.content)) {
    return true
  }

  function hasProductComponent(component) {
    const isContainerComponent = Array.isArray(component.content)

    if (!isContainerComponent) {
      return PRODUCT_RELATED_COMPONENTS.includes(component.meta.component)
    }

    return PRODUCT_RELATED_COMPONENTS.includes(component.meta.component) || component.content.some(hasProductComponent)
  }

  return screen.content.some(hasProductComponent)
}
